import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ERPAnalyticsHeader from "../components/ERPAnalyticsHeader";
import { api } from "../lib/api";
import { useAuth, canAtLeast } from "../lib/auth";
import {
  LayoutDashboard,
  Database,
  Boxes,
  ShieldCheck,
  Crown,
  FileText,
  Search,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  ArrowRight,
  Activity,
  Factory,
  ShoppingCart,
  PackageCheck,
  Truck,
  FlaskConical,
  Warehouse,
} from "lucide-react";

const MODULES = [
  { name: "Sales", status: "Planning", icon: ShoppingCart, note: "Buyer, consignee, country, grade, variety, brand, price comparison." },
  { name: "Purchase", status: "Planning", icon: PackageCheck, note: "Supplier, centre, purchase quality, quantity, value, raw material flow." },
  { name: "QA", status: "Planning", icon: FlaskConical, note: "Samples, antibiotic tests, rejected batches, supplier traceability." },
  { name: "Process", status: "Planning", icon: Factory, note: "Production, yield, peeling, VA, soaking, IQF/block freeze." },
  { name: "Store", status: "Planning", icon: Warehouse, note: "Coldstore stock, availability, dummy cases, material and chemical stock." },
  { name: "PO / Dispatch", status: "Planning", icon: Truck, note: "Pending POs, confirmed orders, shipment, on-water, FDA, delivery promptness." },
];

const VALIDATIONS = [
  "Buyer vs consignee mapping confirmation",
  "Sales value, sale price, and payment formulas",
  "Purchase value in lakhs/crores formula",
  "Confirmed order reverse raw material calculation",
  "Batch-wise QA test history without overwrite",
  "On-time, early, delayed, on-water shipment rules",
];

export default function Home() {
  const { user } = useAuth();
  const nav = useNavigate();

  const [dashboards, setDashboards] = useState([]);
  const [connections, setConnections] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [reports, setReports] = useState([]);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    api.get("/dashboards").then((r) => setDashboards(r.data.dashboards || [])).catch(() => {});
    api.get("/connections").then((r) => setConnections(r.data.connections || [])).catch(() => {});
    api.get("/datasets").then((r) => setDatasets([...(r.data.datasets || []), ...(r.data.demo_datasets || [])])).catch(() => {});
    api.get("/reports").then((r) => setReports(r.data.reports || [])).catch(() => {});

    if (canAtLeast(user, "workspace_admin")) {
      api.get("/audit-logs?limit=6").then((r) => setAudit(r.data.logs || [])).catch(() => {});
    }
  }, [user]);

  const liveConnections = connections.filter((c) => c.status === "ok").length;

  return (
    <div className="erp-home-shell">
      <ERPAnalyticsHeader />

      <main className="erp-content">
        <div className="erp-page-title-row">
          <div>
            <h1>Camaroo Analytics Super Admin</h1>
            <p>
              Clean V2 control panel for rebuilding the CEO Command Center, reports, datasets,
              role-based dashboards, and client-ready analytics flow.
            </p>
          </div>

          <button className="erp-primary-btn" onClick={() => nav("/ceo-command-center")}>
            <Crown size={15} strokeWidth={1.8} />
            Open CEO Command Center
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
          <StatCard label="Active Dashboards" value={dashboards.length} note="Fresh V2 dashboard list" icon={LayoutDashboard} />
          <StatCard label="Live Connections" value={`${liveConnections}/${connections.length}`} note="ERP and upload sources" icon={Database} />
          <StatCard label="Datasets" value={datasets.length} note="Reusable analytics models" icon={Boxes} />
          <StatCard label="Reports" value={reports.length} note="Downloadable business views" icon={FileText} />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-5">
          <div className="xl:col-span-2 erp-card">
            <div className="erp-panel-head">
              <div>
                <h2>CEO Command Center Rebuild Plan</h2>
                <p>Module-wise daily business visibility for management meetings and decision tracking.</p>
              </div>
              <span className="erp-status-chip warning">Rebuild Started</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              {MODULES.map((m) => {
                const Icon = m.icon;
                return (
                  <div key={m.name} className="erp-module-card">
                    <div className="erp-module-icon"><Icon size={18} strokeWidth={1.8} /></div>
                    <div>
                      <div className="erp-module-title">{m.name}</div>
                      <div className="erp-module-note">{m.note}</div>
                      <span className="erp-status-chip small">{m.status}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="erp-card">
            <div className="erp-panel-head">
              <div>
                <h2>Current V2 Status</h2>
                <p>Clean rebuild progress.</p>
              </div>
            </div>

            <div className="erp-status-list">
              <StatusRow icon={CheckCircle2} label="Old dashboards archived" value="169 legacy dashboards safe" good />
              <StatusRow icon={CheckCircle2} label="Active dashboard list cleaned" value="0 active dashboards" good />
              <StatusRow icon={Activity} label="ERP-style top menu" value="Applied across main screens" good />
              <StatusRow icon={Clock3} label="CEO formulas" value="Business validation pending" />
              <StatusRow icon={AlertTriangle} label="SSL" value="Not secure yet" danger />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="erp-card">
            <div className="erp-panel-head">
              <div>
                <h2>Pending Business Validations</h2>
                <p>These must be confirmed before final CEO charts and KPIs.</p>
              </div>
            </div>

            <div className="erp-check-list">
              {VALIDATIONS.map((item) => (
                <div key={item} className="erp-check-item">
                  <AlertTriangle size={14} strokeWidth={1.8} />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="erp-card">
            <div className="erp-panel-head">
              <div>
                <h2>Quick Actions</h2>
                <p>Most-used admin actions for the rebuild.</p>
              </div>
            </div>

            <div className="erp-action-grid">
              <ActionButton icon={Crown} label="CEO Command Center" onClick={() => nav("/ceo-command-center")} />
              <ActionButton icon={Database} label="Data Sources" onClick={() => nav("/data-sources")} />
              <ActionButton icon={Boxes} label="Datasets" onClick={() => nav("/datasets")} />
              <ActionButton icon={Search} label="SQL Editor" onClick={() => nav("/sql")} />
              <ActionButton icon={LayoutDashboard} label="New Dashboard" onClick={() => nav("/builder/new")} />
              <ActionButton icon={ShieldCheck} label="Access Management" onClick={() => nav("/access-management")} />
            </div>
          </div>

          <div className="erp-card">
            <div className="erp-panel-head">
              <div>
                <h2>Recent Activity</h2>
                <p>Latest platform actions.</p>
              </div>
            </div>

            <div className="erp-activity-list">
              {audit.length === 0 && (
                <div className="erp-empty-state">No recent activity available.</div>
              )}

              {audit.map((a) => (
                <div key={a.id} className="erp-activity-row">
                  <div>
                    <strong>{a.action}</strong>
                    <span>{a.user_email || "System"}</span>
                  </div>
                  <small>{new Date(a.created_at).toLocaleString()}</small>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, note, icon: Icon }) {
  return (
    <div className="erp-stat-card">
      <div>
        <span>{label}</span>
        <strong>{value ?? "—"}</strong>
        <small>{note}</small>
      </div>
      <div className="erp-stat-icon">
        <Icon size={19} strokeWidth={1.8} />
      </div>
    </div>
  );
}

function StatusRow({ icon: Icon, label, value, good, danger }) {
  return (
    <div className={`erp-status-row ${good ? "good" : ""} ${danger ? "danger" : ""}`}>
      <Icon size={15} strokeWidth={1.8} />
      <div>
        <strong>{label}</strong>
        <span>{value}</span>
      </div>
    </div>
  );
}

function ActionButton({ icon: Icon, label, onClick }) {
  return (
    <button type="button" className="erp-action-btn" onClick={onClick}>
      <Icon size={15} strokeWidth={1.8} />
      <span>{label}</span>
      <ArrowRight size={13} strokeWidth={1.8} />
    </button>
  );
}
